export interface GeojsonValidationResult {
  valid: boolean
  error?: string
}

const GEOMETRY_TYPES = new Set([
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
])

/**
 * Validate that a string holds a GeoJSON FeatureCollection the app is able to render
 * (non-empty features array, every feature a real Feature with a usable geometry).
 */
export function validateGeojsonString(content: string): GeojsonValidationResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(content)
  } catch {
    return { valid: false, error: 'The file is not valid JSON.' }
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { valid: false, error: 'The file is not a GeoJSON object.' }
  }

  const geojson = parsed as Record<string, unknown>
  if (geojson['type'] !== 'FeatureCollection') {
    return { valid: false, error: 'The GeoJSON must be a FeatureCollection.' }
  }

  const features = geojson['features']
  if (!Array.isArray(features) || features.length === 0) {
    return { valid: false, error: 'The FeatureCollection contains no features.' }
  }

  for (let i = 0; i < features.length; i++) {
    const error = _validateFeature(features[i], i)
    if (error) return { valid: false, error }
  }

  return { valid: true }
}

/** Read a File and validate its GeoJSON content. */
export async function validateGeojsonFile(file: File): Promise<GeojsonValidationResult> {
  let text: string
  try {
    text = await file.text()
  } catch {
    return { valid: false, error: 'The file could not be read.' }
  }
  return validateGeojsonString(text)
}

function _validateFeature(feature: unknown, index: number): string | null {
  const position = index + 1
  if (typeof feature !== 'object' || feature === null) {
    return `Feature ${position} is not a valid GeoJSON Feature.`
  }

  const f = feature as Record<string, unknown>
  if (f['type'] !== 'Feature') {
    return `Feature ${position} is missing "type": "Feature".`
  }

  const geometry = f['geometry']
  if (geometry === null || typeof geometry !== 'object') {
    return `Feature ${position} has a missing or invalid geometry.`
  }

  const g = geometry as Record<string, unknown>
  if (typeof g['type'] !== 'string' || !GEOMETRY_TYPES.has(g['type'])) {
    return `Feature ${position} has an unknown geometry type.`
  }

  if (g['type'] === 'GeometryCollection') {
    if (!Array.isArray(g['geometries'])) {
      return `Feature ${position} geometry collection has no "geometries" array.`
    }
  } else if (!Array.isArray(g['coordinates'])) {
    return `Feature ${position} geometry has no "coordinates" array.`
  }

  return null
}
